import React, { useMemo, useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "react-toastify";
import { API_URL } from "../../constant";
import "./ManualTopCoinWindow.css";

const TOPCOIN_RULE_AMOUNT = 5;
const MTOPCOIN_PER_CURRENCY = 100;

const getDefaultDateTime = () => {
  const now = new Date();
  const tzOffsetMs = now.getTimezoneOffset() * 60000;
  return new Date(now.getTime() - tzOffsetMs).toISOString().slice(0, 16);
};

export const calculateReward = (transactionTotal) => {
  const amount = Number(transactionTotal) || 0;
  const topcoin = Math.floor(amount / TOPCOIN_RULE_AMOUNT);
  const remainder = amount % TOPCOIN_RULE_AMOUNT;
  const mTopcoin = Math.floor(remainder * MTOPCOIN_PER_CURRENCY);

  return { topcoin, mTopcoin };
};

function ManualTopCoinWindow() {
  const outletContext = useOutletContext();
  const userData = outletContext?.userData;
  const token = localStorage.getItem("auth_token");
  const [resolvedRetailBusinessId, setResolvedRetailBusinessId] = useState(null);
  const [isLoadingRetailBusiness, setIsLoadingRetailBusiness] = useState(false);

  const [formData, setFormData] = useState({
    customer_no: "",
    transaction_total: "",
    invoice_no: "",
    transaction_datetime: getDefaultDateTime(),
    product_promotion_code: "",
    manual_override_reason: "",
  });
  const [manualTopcoin, setManualTopcoin] = useState("");
  const [manualMTopcoin, setManualMTopcoin] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [responseData, setResponseData] = useState(null);

  // Pre-fetch retail business ID when component mounts
  useEffect(() => {
    const fetchRetailBusinessId = async () => {
      // First check if userData has the ID
      if (userData?.retail_business_id || userData?.id) {
        const id = userData.retail_business_id || userData.id;
        console.log("Using userData ID:", id);
        setResolvedRetailBusinessId(id);
        return;
      }

      if (!token) {
        console.error("No auth token found");
        toast.error("Authentication token missing. Please log in again.");
        return;
      }

      if (resolvedRetailBusinessId !== null) return;

      setIsLoadingRetailBusiness(true);
      try {
        console.log("Fetching retail business info from API...");
        const res = await fetch(`${API_URL}/api/retail-business/me`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        });

        console.log("API Response status:", res.status);

        if (!res.ok) {
          const errorText = await res.text();
          console.error("API Error:", errorText);
          throw new Error(`API returned ${res.status}: ${errorText}`);
        }

        const data = await res.json();
        console.log("API Response data:", data);

        // Try multiple possible paths for the user ID
        const id = data?.user?.retail_business_id || 
                   data?.user?.id || 
                   data?.retail_business_id || 
                   data?.user?.user_id ||
                   data?.id;

        if (!id) {
          console.error("No ID found in response:", data);
          throw new Error("Could not find retail business ID in response");
        }

        console.log("Resolved retail business ID:", id);
        setResolvedRetailBusinessId(id);
      } catch (error) {
        console.error("Failed to fetch retail business:", error);
        toast.error("Unable to verify retail business account. Please contact support.");
      } finally {
        setIsLoadingRetailBusiness(false);
      }
    };

    fetchRetailBusinessId();
  }, [userData, token]);

  const calculatedReward = useMemo(
    () => calculateReward(formData.transaction_total),
    [formData.transaction_total]
  );

  const overrideTopcoin =
    manualTopcoin === "" ? calculatedReward.topcoin : Number(manualTopcoin);
  const overrideMTopcoin =
    manualMTopcoin === "" ? calculatedReward.mTopcoin : Number(manualMTopcoin);
  const hasManualOverride =
    overrideTopcoin !== calculatedReward.topcoin ||
    overrideMTopcoin !== calculatedReward.mTopcoin;

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    // Debug logging
    console.log("Submit triggered. State:", {
      resolvedRetailBusinessId,
      isLoadingRetailBusiness,
      hasToken: !!token,
      userData: userData,
      formData: formData
    });

    // Wait for retail business ID to be resolved if still loading
    if (isLoadingRetailBusiness) {
      toast.info("Please wait, verifying retail business account...");
      return;
    }

    // Get retail business ID
    let retailBusinessId = resolvedRetailBusinessId;
    
    // If still not resolved, try to get it from userData directly
    if (!retailBusinessId && userData) {
      retailBusinessId = userData.retail_business_id || userData.id;
      console.log("Using userData fallback:", retailBusinessId);
    }

    // If still no ID, try one more API call
    if (!retailBusinessId && token) {
      toast.info("Verifying account details...");
      try {
        const res = await fetch(`${API_URL}/api/retail-business/me`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        });

        if (res.ok) {
          const data = await res.json();
          retailBusinessId = data?.user?.retail_business_id || 
                            data?.user?.id || 
                            data?.retail_business_id;
          if (retailBusinessId) {
            setResolvedRetailBusinessId(retailBusinessId);
          }
        }
      } catch (error) {
        console.error("Fallback API call failed:", error);
      }
    }

    if (!retailBusinessId) {
      console.error("No retail business ID available");
      toast.error("Unable to verify retail business account. Please ensure you are logged in as a retail business user.");
      return;
    }

    if (hasManualOverride && !formData.manual_override_reason.trim()) {
      toast.error("Please add a reason for manual override.");
      return;
    }

    setIsSubmitting(true);
    setResponseData(null);

    try {
      const payload = {
        retail_business_id: Number(retailBusinessId),
        customer_no: formData.customer_no.trim(),
        transaction_total: Number(formData.transaction_total),
        invoice_no: formData.invoice_no.trim(),
        transaction_datetime: new Date(formData.transaction_datetime).toISOString(),
        product_promotion_code: formData.product_promotion_code.trim() || null,
      };

      if (hasManualOverride) {
        payload.manual_awarding_topcoin = overrideTopcoin;
        payload.manual_awarding_mtopcoin = overrideMTopcoin;
        payload.manual_override_reason = formData.manual_override_reason.trim();
      }

      console.log("Submitting payload:", payload);

      const response = await fetch(`${API_URL}/api/retail-business/transaction`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      console.log("Response data:", data);

      if (!response.ok || !data.success) {
        const firstError = data?.errors
          ? Object.values(data.errors)[0]?.[0]
          : data?.message;
        throw new Error(firstError || "Transaction submission failed.");
      }

      setResponseData(data.data);
      toast.success("Transaction completed successfully.");
      
      // Reset form? Optional
      // setFormData({
      //   ...formData,
      //   transaction_total: "",
      //   invoice_no: "",
      // });
      
    } catch (error) {
      console.error("Transaction error:", error);
      toast.error(error.message || "Failed to complete transaction.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Format current date & time for header display
  const now = new Date();
  const displayDate = now.toLocaleDateString("en-GB");
  const displayTime = now.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });

  // Compute remainder value for display (£ value of mTopcoin portion)
  const transactionAmount = Number(formData.transaction_total) || 0;
  const remainderValue = (transactionAmount % TOPCOIN_RULE_AMOUNT).toFixed(2);

  if (isLoadingRetailBusiness) {
    return (
      <div className="mtcw-loading">
        <p>Loading retail business information...</p>
      </div>
    );
  }

  return (
    <div className="mtcw-outer">
      {/* Dark purple decorative border frame */}
      <div className="mtcw-border-frame">
        {/* White receipt card */}
        <div className="mtcw-card">

          {/* ---- Header ---- */}
          <div className="mtcw-header">
            <div className="mtcw-header-left">
              <h3>TopCoin UK<br />(TCUK)</h3>
            </div>
            <div className="mtcw-header-right">
              {displayDate}{" "}{displayTime}
            </div>
          </div>

          {/* ---- Branding row ---- */}
          <div className="mtcw-branding">
            <div className="mtcw-branding-center">
              <p className="brand-name">TopCoinLand</p>
              <a
                className="brand-link"
                href="https://www.topcoinland.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                www.topcoinland.com
              </a>
            </div>
            {/* <div className="mtcw-branding-right">
              {resolvedRetailBusinessId || "—"}
            </div> */}
          </div>

          {/* <hr className="mtcw-divider" /> */}

          {/* ---- Form ---- */}
          <form onSubmit={handleSubmit}>

            {/* Customer No */}
            <div className="mtcw-form-row">
              <span className="label-text">
                Customer No:
              </span>
              <input
                className="field-input"
                name="customer_no"
                value={formData.customer_no}
                onChange={handleChange}
                required
              />
            </div>

            {/* Transaction Total */}
            <div className="mtcw-form-row">
              <span className="label-text">Transaction Total:</span>
              <input
                className="field-input currency-input"
                name="transaction_total"
                type="number"
                min="0"
                step="0.01"
                placeholder="£0.00"
                value={formData.transaction_total}
                onChange={handleChange}
                required
              />
            </div>

            {/* Awarding TopCoin */}
            <div className="mtcw-award-row">
              <span className="label-text">Awarding<br />TopCoin:</span>
              <input
                className="field-input"
                value={calculatedReward.topcoin}
                disabled
              />
            </div>

            {/* Awarding M-TopCoin */}
            <div className="mtcw-award-row">
              <span className="label-text">Awarding M-TopCoin:</span>
              <input
                className="field-input"
                value={calculatedReward.mTopcoin}
                disabled
              />
            </div>

            {/* Invoice No */}
            <div className="mtcw-form-row">
              <span className="label-text">Invoice No</span>
              <input
                className="field-input"
                name="invoice_no"
                value={formData.invoice_no}
                onChange={handleChange}
                required
              />
            </div>

            {/* Hidden datetime — still submitted but not shown in receipt layout */}
            <input
              type="hidden"
              name="transaction_datetime"
              value={formData.transaction_datetime}
            />

            {/* Manual override section (appears only when values differ) */}
            {hasManualOverride && (
              <div className="mtcw-override-section">
                <label>
                  Manual Override Reason
                  <textarea
                    name="manual_override_reason"
                    value={formData.manual_override_reason}
                    onChange={handleChange}
                    placeholder="Please provide a reason for the manual override..."
                  />
                </label>
              </div>
            )}

            {/* Submit */}
            <div className="mtcw-submit-area">
              <button
                type="submit"
                className="mtcw-submit-btn"
                disabled={isSubmitting || !resolvedRetailBusinessId}
              >
                {isSubmitting ? "PROCESSING..." : "COMPLETE TRANSACTION"}
              </button>
            </div>
          </form>

          {/* ---- Response ---- */}
          {responseData && (
            <div className="mtcw-response">
              <p>
                <strong>SBC Ref:</strong> {responseData.sbc_ref}
              </p>
              <p>
                <strong>Awarded:</strong>{" "}
                {responseData.awarding_topcoin} TopCoin /{" "}
                {responseData.awarding_mtopcoin} M-TopCoin
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ManualTopCoinWindow;